import { computed, getCurrentInstance } from "@vue/composition-api";
import { intervalToDuration } from "date-fns";
import { AgeVerificationStatus } from "@scrile/api-provider/dist/api/AgeVerificationProvider";
import useAuthUser from "@/hooks/useAuthUser";
import { MUTATION_TYPES } from "@/store/const";
import { StateRoot } from "@/store/state";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { getStorageNSFWUserSettings, setNSFWUserSettings } from "@/lib/nsfwHelpers";
import config from "@/config";
import { EVENTS } from "@/const";

export default function useNSFWUserSettings() {
  const vm = getCurrentInstance();
  const { authUser } = useAuthUser();
  const nSFWUserSettings = computed(() => (vm ? ((vm.$store.state as StateRoot).nSFWUserSettings ?? null) : null));
  const showNotSafeForWork = computed(() => (!config.featureNSFW ? true : !!nSFWUserSettings.value));

  const updateNSFWUserSettings = async (value: boolean) => {
    if (authUser.value) {
      await providers.NSFWSettingsProvider.updateUserSettings({
        data: { userId: authUser.value?.id, showNotSafeForWork: value },
      });
    } else {
      setNSFWUserSettings(value);
    }
    vm?.$store.commit(MUTATION_TYPES.SET_NSFW_USER_SETTING, value);
  };

  const canEnableNSFWByDefaultFlow = async () => {
    if (!authUser.value) return false;
    const response = await providers.PersonalInformationProvider.find({ id: authUser.value.id });
    if (!response?.dateOfBirth) return false;
    const { years = 0 } = intervalToDuration({ start: new Date(response.dateOfBirth), end: new Date(Date.now()) });
    return years > 18;
  };

  const canEnableNSFWByYotiFlow = async () => {
    if (!authUser.value) return false;
    const response = await providers.AgeVerificationProvider.find({ userId: authUser.value.id });
    return response?.status === AgeVerificationStatus.APPROVED;
  };

  const canEnableNSFW = async () => {
    if (!authUser.value) return true;
    if (config.featureYotiAgeVerification) return canEnableNSFWByYotiFlow();
    return canEnableNSFWByDefaultFlow();
  };

  const toggleNSFWUserSettings = async () => {
    if (nSFWUserSettings.value === null) {
      emitter.emit(EVENTS.SENSITIVE_CONTENT_GATE_REQUEST, true);
      return;
    }
    const value = !nSFWUserSettings.value;
    if (value) {
      try {
        const canEnable = await canEnableNSFW();
        if (!canEnable) {
          emitter.emit(EVENTS.SENSITIVE_CONTENT_GATE_REQUEST, true);
          return;
        }
      } catch {
        emitter.emit(EVENTS.SENSITIVE_CONTENT_GATE_REQUEST, true);
        return;
      }
    }

    await updateNSFWUserSettings(value);
  };

  const init = () => {
    if (!authUser.value) {
      const value = getStorageNSFWUserSettings();
      vm?.$store.commit(MUTATION_TYPES.SET_NSFW_USER_SETTING, value);
    }
  };

  return {
    showNotSafeForWork,
    nSFWUserSettings,
    init,
    toggleNSFWUserSettings,
    updateNSFWUserSettings,
  };
}
